<script setup>
import {computed, onMounted, onUnmounted, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'

import PageEditorToolbar from "./PageEditorToolbar.vue"
import PageEditorIframe from "./PageEditorIframe.vue"
import PageEditorLeftSidebar from "./PageEditorLeftSidebar.vue"
import PageEditorRightSidebar from "./PageEditorRightSidebar.vue"
import PageEditorAddContentOffcanvas from "./PageEditorAddContentOffcanvas.vue"
import PageEditorAddExistingContentOffcanvas from "./PageEditorAddExistingContentOffcanvas.vue"

import {usePageEditorStore} from '@/stores/pageEditor'
import {useAppStore} from '@/stores/appStore'

const route = useRoute()
const router = useRouter()
const pageEditorStore = usePageEditorStore()
const appStore = useAppStore()

const pageId = computed(() => pageEditorStore.pageId)
const contentAreas = computed(() => pageEditorStore.contentAreas)
const hoverSectionId = computed(() => pageEditorStore.hoverSectionId)
const hoverContentAreaName = computed(() => pageEditorStore.hoverContentAreaName)
const hoverPageContentId = computed(() => pageEditorStore.hoverPageContentId)

const deleteEvent = (e) => {
    pageEditorStore.keyupEventHandler(e)
}

const setSectionBackground = (id, colour) => {
    if (id) {
        const target = pageEditorStore.pageSections.find(o => o.id === id)
        if (target && target.el) target.el.style.background = colour
    }
}

const setAreaBackground = (name, colour) => {
    if (name) {
        const target = pageEditorStore.contentAreas.find(o => o.name === name)
        if (target && target.el) target.el.style.background = colour
    }
}

const setContentBackground = (id, colour) => {
    if (id) {
        const o = pageEditorStore.contentItems.find(o => o.pageContentId === id)
        if (o && o.el) {
            o.el.style.background = colour
        }
    }
}

watch(() => route.params, (params) => {
    const pageIdVal = params?.pageId ? parseInt(params.pageId) : 0
    const sectionId = params?.sectionId ? parseInt(params.sectionId) : 0
    const areaIndex = params?.areaIndex !== undefined ? params.areaIndex : -1
    const pageContentId = params?.pageContentId ? parseInt(params.pageContentId) : 0

    pageEditorStore.pageId = pageIdVal
    pageEditorStore.activeSectionId = 0
    pageEditorStore.activeContentAreaName = ''
    pageEditorStore.activePageContentId = 0

    switch (true) {
        case pageContentId > 0:
            pageEditorStore.activePageContentId = pageContentId
            break
        case areaIndex > -1:
            if (contentAreas.value[areaIndex]) {
                pageEditorStore.activeContentAreaName = contentAreas.value[areaIndex].name
            }
            break
        case sectionId > 0:
            pageEditorStore.activeSectionId = sectionId
    }
}, {immediate: true, deep: true})

watch(hoverSectionId, (newValue, oldValue) => {
    setSectionBackground(oldValue, '')
    setSectionBackground(newValue, 'rgba(9, 110, 253, 0.2)')
})

watch(hoverContentAreaName, (newValue, oldValue) => {
    setAreaBackground(oldValue, '')
    setAreaBackground(newValue, 'rgba(9, 110, 253, 0.2)')
})

watch(hoverPageContentId, (newValue, oldValue) => {
    setContentBackground(oldValue, '')
    setContentBackground(newValue, 'rgba(9, 110, 253, 0.2)')
})

appStore.navigationComponent = 'editor'
pageEditorStore.loadSettings()

const params = route.params
if (params.pageId) {
    pageEditorStore.pageId = parseInt(params.pageId)
} else {
    const fallbackPageId = pageId.value || appStore.settings.homePage
    router.push({name: 'pageEditor', params: {pageId: fallbackPageId}})
}

onMounted(() => {
    document.addEventListener('keyup', deleteEvent)
})

onUnmounted(() => {
    document.removeEventListener('keyup', deleteEvent)
})
</script>

<template>
    <div class="d-flex flex-column h-100 overflow-hidden">
        <PageEditorToolbar/>

        <PageEditorAddContentOffcanvas/>
        <PageEditorAddExistingContentOffcanvas/>

        <div ref="container" class="flex-fill d-flex h-100 overflow-hidden">
            <PageEditorLeftSidebar/>

            <div class="flex-fill position-relative overflow-hidden w-100 h-100 d-flex">
                <PageEditorIframe v-if="pageId" :page-id="pageId" :key="pageId"/>
            </div>

            <PageEditorRightSidebar/>
        </div>
    </div>
</template>